// Znalostní báze SEO Tools — expertní vysvětlení ke každému doporučení.
// Klíčem je `id` shodné s id checků (SEO analýza), opportunities (PageSpeed),
// GEO a UX auditu. Sdílí ho popup (KnowledgeModal) i stránka dokumentace.
//
// Pozn. k i18n: dlouhý odborný obsah držíme zde (čeština první) kvůli
// udržovatelnosti; krátké popisky UI (nadpisy sekcí, tlačítka) jdou přes i18n
// klíče. Překlady do dalších jazyků se doplní dávkou později. Viz CLAUDE.md.

import kbEn from './knowledgeBase.en.json'
import kbDe from './knowledgeBase.de.json'
import kbPl from './knowledgeBase.pl.json'
import kbSk from './knowledgeBase.sk.json'
import kbIt from './knowledgeBase.it.json'
import kbHu from './knowledgeBase.hu.json'
import kbUk from './knowledgeBase.uk.json'
import kbEs from './knowledgeBase.es.json'
import kbFr from './knowledgeBase.fr.json'
import kbNl from './knowledgeBase.nl.json'
import kbJa from './knowledgeBase.ja.json'

export type KbCategory = 'seo' | 'performance' | 'geo' | 'ux' | 'security'
export type KbImpact = 'critical' | 'high' | 'medium' | 'low'
export type KbDifficulty = 'easy' | 'medium' | 'hard'

// Aktivní jazyk je při načtení fixní (přepínač jazyka dělá plný reload),
// takže obsah znalostní báze lokalizujeme jednou při načtení modulu.
const KB_PREFIX = ['en', 'de', 'pl', 'sk', 'it', 'hu', 'uk', 'es', 'fr', 'nl', 'ja']
function kbActiveLang(): string {
  if (typeof window === 'undefined') return 'cs'
  const seg = window.location.pathname.split('/')[1] || ''
  if (KB_PREFIX.includes(seg)) return seg
  const stored = (localStorage.getItem('i18nextLng') || '').slice(0, 2)
  if (KB_PREFIX.includes(stored)) return stored
  // Na cs webu je bez prefixu → seg je NÁZEV STRÁNKY (např. „napoveda"), NE jazyk.
  // Proto fallback řeš jen podle uloženého jazyka; neznámý/cizí bez KB obsahu → en, jinak cs.
  return (stored && stored !== 'cs') ? 'en' : 'cs'
}
const KB_LANG = kbActiveLang()

// Lokalizované popisky kategorií / dopadu / náročnosti
const CAT_L: Record<string, Record<KbCategory, string>> = {
  cs: { seo: 'SEO', performance: 'Rychlost', geo: 'GEO / AI', ux: 'UX / UI', security: 'Bezpečnost' },
  en: { seo: 'SEO', performance: 'Speed', geo: 'GEO / AI', ux: 'UX / UI', security: 'Security' },
  de: { seo: 'SEO', performance: 'Geschwindigkeit', geo: 'GEO / AI', ux: 'UX / UI', security: 'Sicherheit' },
  pl: { seo: 'SEO', performance: 'Szybkość', geo: 'GEO / AI', ux: 'UX / UI', security: 'Bezpieczeństwo' },
  sk: { seo: 'SEO', performance: 'Rýchlosť', geo: 'GEO / AI', ux: 'UX / UI', security: 'Bezpečnosť' },
  it: { seo: 'SEO', performance: 'Velocità', geo: 'GEO / AI', ux: 'UX / UI', security: 'Sicurezza' },
  hu: { seo: 'SEO', performance: 'Sebesség', geo: 'GEO / AI', ux: 'UX / UI', security: 'Biztonság' },
  uk: { seo: 'SEO', performance: 'Швидкість', geo: 'GEO / AI', ux: 'UX / UI', security: 'Безпека' },
  es: { seo: 'SEO', performance: 'Velocidad', geo: 'GEO / AI', ux: 'UX / UI', security: 'Seguridad' },
  fr: { seo: 'SEO', performance: 'Vitesse', geo: 'GEO / AI', ux: 'UX / UI', security: 'Sécurité' },
  nl: { seo: 'SEO', performance: 'Snelheid', geo: 'GEO / AI', ux: 'UX / UI', security: 'Beveiliging' },
  ja: { seo: 'SEO', performance: '速度', geo: 'GEO / AI', ux: 'UX / UI', security: 'セキュリティ' },
}
const IMP_L: Record<string, Record<KbImpact, string>> = {
  cs: { critical: 'Kritický dopad', high: 'Vysoký dopad', medium: 'Střední dopad', low: 'Nízký dopad' },
  en: { critical: 'Critical impact', high: 'High impact', medium: 'Medium impact', low: 'Low impact' },
  de: { critical: 'Kritische Auswirkung', high: 'Hohe Auswirkung', medium: 'Mittlere Auswirkung', low: 'Geringe Auswirkung' },
  pl: { critical: 'Krytyczny wpływ', high: 'Wysoki wpływ', medium: 'Średni wpływ', low: 'Niski wpływ' },
  sk: { critical: 'Kritický dopad', high: 'Vysoký dopad', medium: 'Stredný dopad', low: 'Nízky dopad' },
  it: { critical: 'Impatto critico', high: 'Impatto alto', medium: 'Impatto medio', low: 'Impatto basso' },
  hu: { critical: 'Kritikus hatás', high: 'Magas hatás', medium: 'Közepes hatás', low: 'Alacsony hatás' },
  uk: { critical: 'Критичний вплив', high: 'Високий вплив', medium: 'Середній вплив', low: 'Низький вплив' },
  es: { critical: 'Impacto crítico', high: 'Impacto alto', medium: 'Impacto medio', low: 'Impacto bajo' },
  fr: { critical: 'Impact critique', high: 'Impact élevé', medium: 'Impact moyen', low: 'Impact faible' },
  nl: { critical: 'Kritieke impact', high: 'Hoge impact', medium: 'Gemiddelde impact', low: 'Lage impact' },
  ja: { critical: '重大な影響', high: '大きな影響', medium: '中程度の影響', low: '小さな影響' },
}
const DIF_L: Record<string, Record<KbDifficulty, string>> = {
  cs: { easy: 'Snadné', medium: 'Střední', hard: 'Náročné' },
  en: { easy: 'Easy', medium: 'Medium', hard: 'Hard' },
  de: { easy: 'Einfach', medium: 'Mittel', hard: 'Schwer' },
  pl: { easy: 'Łatwe', medium: 'Średnie', hard: 'Trudne' },
  sk: { easy: 'Ľahké', medium: 'Stredné', hard: 'Náročné' },
  it: { easy: 'Facile', medium: 'Medio', hard: 'Difficile' },
  hu: { easy: 'Egyszerű', medium: 'Közepes', hard: 'Nehéz' },
  uk: { easy: 'Просте', medium: 'Середнє', hard: 'Складне' },
  es: { easy: 'Fácil', medium: 'Medio', hard: 'Difícil' },
  fr: { easy: 'Facile', medium: 'Moyen', hard: 'Difficile' },
  nl: { easy: 'Makkelijk', medium: 'Gemiddeld', hard: 'Moeilijk' },
  ja: { easy: '簡単', medium: '中程度', hard: '難しい' },
}
const catL = (k: KbCategory) => (CAT_L[KB_LANG] || CAT_L.cs)[k]
const impL = (k: KbImpact) => (IMP_L[KB_LANG] || IMP_L.cs)[k]
const difL = (k: KbDifficulty) => (DIF_L[KB_LANG] || DIF_L.cs)[k]

export interface KbExample {
  caption?: string
  /** Špatně / před opravou */
  bad?: string
  /** Správně / po opravě */
  good?: string
  /** Obecná ukázka, když nejde o dvojici bad/good */
  code?: string
}

export interface KbEntry {
  id: string
  category: KbCategory
  title: string
  /** Jedna věta do hlavičky popupu */
  short: string
  impact: KbImpact
  difficulty: KbDifficulty
  /** Odhad času na opravu, lidsky */
  effort?: string
  /** Co to je — vysvětlení pojmu pro začátečníka */
  what: string
  /** Proč to řešit — dopad na SEO / uživatele / vyhledávače */
  why: string
  /** Jak na to — konkrétní kroky */
  how: string[]
  examples?: KbExample[]
  /** Kompletní závěr / shrnutí k bodu */
  conclusion: string
  /** Související témata (id) */
  related?: string[]
}

export const KB_CATEGORIES: Record<KbCategory, { label: string; color: string; icon: string }> = {
  seo:         { label: catL('seo'),         color: 'indigo',  icon: 'Search' },
  performance: { label: catL('performance'), color: 'amber',   icon: 'Gauge' },
  geo:         { label: catL('geo'),         color: 'violet',  icon: 'Sparkles' },
  ux:          { label: catL('ux'),          color: 'cyan',    icon: 'MousePointerClick' },
  security:    { label: catL('security'),    color: 'emerald', icon: 'ShieldCheck' },
}

export const IMPACT_META: Record<KbImpact, { label: string; weight: number; color: string }> = {
  critical: { label: impL('critical'), weight: 4, color: 'red' },
  high:     { label: impL('high'),     weight: 3, color: 'orange' },
  medium:   { label: impL('medium'),   weight: 2, color: 'amber' },
  low:      { label: impL('low'),      weight: 1, color: 'gray' },
}

export const DIFFICULTY_META: Record<KbDifficulty, { label: string; color: string }> = {
  easy:   { label: difL('easy'),   color: 'green' },
  medium: { label: difL('medium'), color: 'amber' },
  hard:   { label: difL('hard'),   color: 'red' },
}

// ─────────────────────────────────────────────────────────────────────────
// SEO ANALÝZA — 18 kontrol (shodné s checkKey v AnalysisCard / SeoAnalyzer)
// ─────────────────────────────────────────────────────────────────────────

const SEO_ENTRIES: KbEntry[] = [
  {
    id: 'https',
    category: 'security',
    title: 'HTTPS / zabezpečené připojení',
    short: 'Web musí běžet na HTTPS s platným certifikátem.',
    impact: 'critical',
    difficulty: 'easy',
    effort: '10–30 min (většinou pár kliknutí u hostingu)',
    what: 'HTTPS je šifrované spojení mezi prohlížečem návštěvníka a serverem. Pozná se podle „https://" a zámečku v adresním řádku. Zajišťuje ho TLS/SSL certifikát.',
    why: 'Google od roku 2014 bere HTTPS jako ranking faktor a Chrome označuje weby bez něj jako „Nezabezpečené", což odrazuje návštěvníky. Bez HTTPS jsou navíc data (formuláře, hesla) přenášena v čitelné podobě a moderní funkce (HTTP/2, geolokace, service workery) nefungují.',
    how: [
      'Zjisti, zda hosting nabízí certifikát zdarma (Let\'s Encrypt) — drtivá většina ano, často na jedno kliknutí.',
      'Aktivuj certifikát pro doménu i variantu www.',
      'Nastav trvalé přesměrování 301 z http:// na https:// (na úrovni serveru, ne až v aplikaci).',
      'Oprav „mixed content" — všechny interní odkazy, obrázky a skripty musí volat https://, ne http://.',
      'Aktualizuj kanonické URL, sitemapu a Search Console na https variantu.',
    ],
    examples: [
      {
        caption: 'Přesměrování http → https v .htaccess (Apache)',
        good: 'RewriteEngine On\nRewriteCond %{HTTPS} off\nRewriteRule ^(.*)$ https://%{HTTP_HOST}%{REQUEST_URI} [L,R=301]',
      },
    ],
    conclusion: 'HTTPS je dnes naprostý základ — bez něj web ztrácí důvěru i pozice a nelze ho považovat za hotový. Náprava je levná (certifikát zdarma) a rychlá. Po nasazení vždy zkontroluj, že nikde nezůstal mixed content a že http verze 301 přesměrovává na https.',
    related: ['http', 'security'],
  },
  {
    id: 'http',
    category: 'security',
    title: 'HTTP stavový kód a dostupnost',
    short: 'Stránka musí vracet kód 200 a být spolehlivě dostupná.',
    impact: 'critical',
    difficulty: 'medium',
    effort: 'dle příčiny (minuty až hodiny)',
    what: 'Při každém načtení server vrací stavový kód: 200 = OK, 301/302 = přesměrování, 404 = nenalezeno, 5xx = chyba serveru. Vyhledávače podle něj poznají, zda stránku indexovat.',
    why: 'Stránky vracející 4xx/5xx Google z indexu vyřadí a ztrácíš na ně návštěvnost. Nadbytečná přesměrování zpomalují načítání a rozmělňují „link juice". Nestabilní dostupnost (občasné 5xx) podkopává důvěru i crawl budget.',
    how: [
      'Ověř, že důležité stránky vracejí přímo 200 bez řetězce přesměrování.',
      'Najdi a oprav 404 u stránek, které mají existovat (překlepy v odkazech, smazaný obsah → 301 na náhradu).',
      'Řeš opakované 5xx — typicky chyba v aplikaci, vyčerpaná paměť nebo přetížený server.',
      'Zkrať řetězce přesměrování na maximálně jeden skok.',
    ],
    conclusion: 'Stabilní kód 200 na klíčových stránkách je podmínkou indexace. 5xx ber jako havarijní stav, 404 na cenných URL jako únik návštěvnosti. Pravidelně kontroluj logy a Search Console (Pokrytí), ať chyby odhalíš dřív než Google.',
    related: ['https', 'redirects', 'broken-links'],
  },
  {
    id: 'title',
    category: 'seo',
    title: 'Title tag (titulek stránky)',
    short: 'Každá stránka potřebuje unikátní, výstižný titulek 50–60 znaků.',
    impact: 'critical',
    difficulty: 'easy',
    effort: '5 min na stránku',
    what: 'Title je text v <title> v hlavičce HTML. Zobrazuje se jako modrý nadpis ve výsledcích vyhledávání a v záložce prohlížeče. Je to jeden z nejsilnějších on-page signálů.',
    why: 'Title má největší váhu z on-page prvků pro relevanci a zásadně ovlivňuje míru prokliku (CTR). Příliš dlouhý se ořízne, prázdný nebo duplicitní mate Google i uživatele a snižuje pozice.',
    how: [
      'Drž délku zhruba 50–60 znaků (cca 560 px), ať se neořízne.',
      'Hlavní klíčové slovo dej co nejvíc dopředu.',
      'Každá stránka musí mít unikátní title — žádné kopie napříč webem.',
      'Přidej značku/brand na konec: „Klíčové slovo — Název webu".',
      'Piš pro člověka: titulek má lákat k prokliku, ne být jen seznam slov.',
    ],
    examples: [
      {
        caption: 'Před a po',
        bad: '<title>Úvod</title>',
        good: '<title>Dětské zimní bundy — výprodej až -50 % | ShopName</title>',
      },
    ],
    conclusion: 'Title je tvoje „reklama ve výsledcích". Investuj do něj: unikátní, s klíčovým slovem vepředu, do 60 znaků a psaný tak, aby se na něj chtělo kliknout. Je to jedna z nejlevnějších úprav s největším efektem na pozice i CTR.',
    related: ['meta_description', 'headings', 'content_quality'],
  },
  {
    id: 'meta_description',
    category: 'seo',
    title: 'Meta description',
    short: 'Krátký popis stránky (120–158 znaků) pod titulkem ve výsledcích.',
    impact: 'high',
    difficulty: 'easy',
    effort: '5 min na stránku',
    what: 'Meta description je atribut v <meta name="description">. Google ji často zobrazuje jako popisek pod titulkem ve výsledcích vyhledávání.',
    why: 'Není přímý ranking faktor, ale výrazně ovlivňuje CTR — dobrý popis přitáhne víc prokliků. Když chybí nebo je slabá, Google si vytáhne náhodný úryvek ze stránky, který nemusí lákat.',
    how: [
      'Délka 120–158 znaků, ať se neořízne.',
      'Zahrň klíčové slovo (Google ho ve výsledcích ztuční) a jasnou výhodu/výzvu.',
      'Každá stránka unikátní description.',
      'Piš jako poutavý mikro-text: co návštěvník získá, proč kliknout.',
    ],
    examples: [
      {
        caption: 'Dobrá meta description',
        good: '<meta name="description" content="Dětské zimní bundy se slevou až 50 %. Voděodolné, teplé, doprava zdarma nad 1000 Kč a vrácení do 30 dnů.">',
      },
    ],
    conclusion: 'Ber description jako reklamní slogan pod titulkem. Nezvedne přímo pozici, ale zvedne proklik — a vyšší CTR z dlouhodobého hlediska pozicím pomáhá. Vždy unikátní, do ~155 znaků, s benefitem a klíčovým slovem.',
    related: ['title', 'open_graph'],
  },
  {
    id: 'meta_keywords',
    category: 'seo',
    title: 'Meta keywords (Seznam.cz)',
    short: 'Klíčová slova v <meta name="keywords">. Google ignoruje, Seznam.cz může zohlednit.',
    impact: 'low',
    difficulty: 'easy',
    effort: '2 min na stránku',
    what: 'Meta keywords je atribut v <meta name="keywords"> s čárkami oddělenými klíčovými slovy stránky. Historicky sloužil k deklaraci témat stránky vyhledávačům.',
    why: 'Google meta keywords oficiálně ignoruje od roku 2009 — kvůli masivnímu zneužívání (keyword stuffing). Na českém trhu ale Seznam.cz tomuto tagu historicky přikládal určitou (dnes malou) váhu, takže pro weby cílící na české publikum má smysl ho vyplnit. Není to ranking faktor s velkým dopadem, spíš drobná nápověda navíc.',
    how: [
      'Vyplň 5–10 relevantních klíčových slov, oddělených čárkami.',
      'Používej slova, která se reálně vyskytují v obsahu stránky.',
      'Nepřeháněj počet — víc než ~15 slov působí jako spam a může uškodit.',
      'Každá stránka by měla mít vlastní, relevantní sadu (ne stejnou všude).',
      'Pro Google to nic neznamená, takže kvůli němu se tím nezdržuj.',
    ],
    examples: [
      {
        caption: 'Rozumné meta keywords',
        good: '<meta name="keywords" content="autoservis praha, opravy aut, stk, pneuservis, výměna oleje">',
      },
      {
        caption: 'Keyword stuffing — spíš uškodí',
        bad: '<meta name="keywords" content="auto, autoservis, servis, opravy, levně, nejlevnější, praha, brno, ostrava, ...30 dalších">',
      },
    ],
    conclusion: 'Meta keywords je dnes okrajová záležitost — Google ho ignoruje úplně. Pokud ale cílíš na český trh přes Seznam.cz, vyplnění 5–10 poctivých klíčových slov neuškodí a může drobně pomoct. Hlavní pravidlo: žádný keyword stuffing.',
    related: ['title', 'meta_description', 'content_quality'],
  },
  {
    id: 'headings',
    category: 'seo',
    title: 'Nadpisy H1–H6 (struktura)',
    short: 'Právě jeden H1 a logická hierarchie podnadpisů.',
    impact: 'high',
    difficulty: 'easy',
    effort: '10–20 min na stránku',
    what: 'Nadpisy H1 až H6 vytvářejí osnovu obsahu. H1 je hlavní nadpis stránky, H2 hlavní sekce, H3 podsekce atd. Tvoří strom, ne náhodné velikosti písma.',
    why: 'Nadpisy pomáhají vyhledávačům i čtečkám pro nevidomé pochopit strukturu a téma stránky. H1 s klíčovým slovem posiluje relevanci. Chybějící H1, více H1 nebo přeskakování úrovní (H2 → H4) strukturu rozbíjí a zhoršuje srozumitelnost.',
    how: [
      'Každá stránka má právě jeden H1, který vystihuje její téma a obsahuje hlavní klíčové slovo.',
      'Nepřeskakuj úrovně — po H2 jde H3, ne rovnou H4.',
      'Používej nadpisy pro strukturu, ne pro vizuální zvětšení textu (na to je CSS).',
      'Podnadpisy piš popisně, ať osnova dává smysl i bez textu mezi nimi.',
    ],
    examples: [
      {
        caption: 'Správná hierarchie',
        good: 'H1: Dětské zimní bundy\n  H2: Jak vybrat velikost\n    H3: Měření podle věku\n    H3: Tabulka velikostí\n  H2: Materiály a údržba',
        bad: 'H1: Vítejte\nH1: Dětské bundy   ← druhý H1\n  H4: Velikosti    ← přeskočené H2/H3',
      },
    ],
    conclusion: 'Nadpisy jsou osnova, ne dekorace. Pravidlo: jeden H1 s klíčovým slovem, dál sestupně bez přeskakování úrovní. Dobrá struktura nadpisů zlepšuje SEO, přístupnost i čitelnost zároveň — je to rychlá výhra.',
    related: ['title', 'content_quality', 'accessibility'],
  },
  {
    id: 'images',
    category: 'seo',
    title: 'Obrázky a alt texty',
    short: 'Obrázky potřebují alt popis, moderní formát a kompresi.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '1–2 min na obrázek',
    what: 'Atribut alt je textový popis obrázku. Čtou ho vyhledávače (nevidí obrázek) i čtečky pro nevidomé a zobrazí se, když se obrázek nenačte.',
    why: 'Alt texty pomáhají rankovat v obrázkovém vyhledávání a zpřístupňují web. Neoptimalizované obrázky (velké JPEG/PNG bez komprese) jsou nejčastější příčina pomalého načítání a horšího skóre rychlosti.',
    how: [
      'Doplň výstižný alt ke každému obsahovému obrázku (dekorativní mohou mít alt="").',
      'Popisuj, co je na obrázku, přirozeně a případně s klíčovým slovem — ne „obrazek1.jpg".',
      'Používej moderní formáty WebP/AVIF místo JPEG/PNG (o 25–50 % menší).',
      'Komprimuj a zmenši na reálně potřebné rozměry; přidej lazy loading pro obrázky mimo viditelnou oblast.',
    ],
    examples: [
      {
        bad: '<img src="DSC_0042.jpg">',
        good: '<img src="detska-zimni-bunda-modra.webp" alt="Dětská zimní bunda v modré barvě s kapucí" loading="lazy" width="600" height="800">',
      },
    ],
    conclusion: 'Obrázky řeš ve dvou rovinách: alt texty (SEO + přístupnost) a optimalizace váhy (rychlost). Obojí je snadné a přináší dvojí užitek. Moderní formát + komprese + lazy loading často samy o sobě výrazně zvednou skóre rychlosti.',
    related: ['lazy-load-images', 'modern-image-formats', 'img-dimensions', 'accessibility'],
  },
  {
    id: 'viewport',
    category: 'ux',
    title: 'Viewport / mobilní zobrazení',
    short: 'Meta viewport zajišťuje správné zobrazení na mobilu.',
    impact: 'critical',
    difficulty: 'easy',
    effort: '2 min',
    what: 'Meta tag viewport říká prohlížeči, jak škálovat stránku na šířku zařízení. Bez něj mobil vykreslí desktopovou šířku a vše je „malé a oddálené".',
    why: 'Google indexuje mobile-first — hodnotí primárně mobilní verzi. Bez správného viewportu web propadne v testu použitelnosti na mobilu, což přímo sráží pozice, protože většina návštěv je z mobilů.',
    how: [
      'Vlož do <head> standardní viewport meta tag.',
      'Nezakazuj zoom (maximum-scale=1 / user-scalable=no) — škodí to přístupnosti.',
      'Ověř, že se obsah nepřetéká vodorovně a tlačítka jsou dost velká (viz UX audit).',
    ],
    examples: [
      { good: '<meta name="viewport" content="width=device-width, initial-scale=1">' },
    ],
    conclusion: 'Jeden řádek v hlavičce, ale zásadní pro mobile-first svět. Bez něj je web na mobilu prakticky nepoužitelný a Google to tvrdě penalizuje. Po nasazení otestuj na reálném telefonu i v nástroji pro mobilní použitelnost.',
    related: ['mobile', 'tap-targets', 'small-fonts'],
  },
  {
    id: 'language',
    category: 'seo',
    title: 'Deklarace jazyka (lang)',
    short: 'Atribut lang na <html> určuje jazyk stránky.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '2 min',
    what: 'Atribut lang na elementu <html> (např. lang="cs") říká prohlížečům a vyhledávačům, v jakém jazyce je obsah.',
    why: 'Pomáhá Googlu zobrazit stránku správnému publiku a správně ji zpracovat (např. nenabízet překlad češtiny Čechům). Čtečky pro nevidomé podle něj volí správnou výslovnost. Bez něj vznikají chyby v cílení i přístupnosti.',
    how: [
      'Nastav lang na <html> podle hlavního jazyka stránky.',
      'U vícejazyčného webu měň lang podle jazyka konkrétní verze a doplň hreflang.',
    ],
    examples: [
      { good: '<html lang="cs">' },
    ],
    conclusion: 'Drobnost s dopadem na cílení i přístupnost. Nastav lang správně podle obsahu stránky; u vícejazyčných webů ho dolaď spolu s hreflang. Náklady nulové, přínos jistý.',
    related: ['hreflang'],
  },
  {
    id: 'canonical',
    category: 'seo',
    title: 'Kanonická URL (canonical)',
    short: 'Říká vyhledávačům, která verze URL je ta hlavní.',
    impact: 'high',
    difficulty: 'medium',
    effort: '10–30 min (nastavení šablony)',
    what: 'Tag <link rel="canonical"> určuje „originální" adresu obsahu. Použije se, když je tentýž obsah dostupný na více URL (parametry, /, www vs. bez www, řazení produktů).',
    why: 'Duplicitní obsah rozmělňuje sílu odkazů a Google může indexovat špatnou verzi. Kanonikalizace sjednotí signály na jednu URL a předejde kanibalizaci a problémům s duplicitou.',
    how: [
      'Na každou stránku dej canonical na její vlastní preferovanou URL (self-referencing).',
      'U filtrovaných/řazených variant ukazuj canonical na základní verzi.',
      'Buď konzistentní: jedna varianta domény (https + www nebo bez), bez koncového lomítka navíc.',
      'Canonical URL musí vracet 200 a být indexovatelná (ne noindex, ne přesměrování).',
    ],
    examples: [
      { good: '<link rel="canonical" href="https://www.example.cz/produkty/bunda">' },
    ],
    conclusion: 'Canonical je nástroj proti duplicitě a roztříštěným signálům. Self-referencing canonical na každé stránce + směrování variant na základní URL je bezpečný standard. Pozor, ať canonical neukazuje omylem na úvodní stránku nebo na noindex verzi — to je častá a drahá chyba.',
    related: ['url_structure', 'robots', 'hreflang'],
  },
  {
    id: 'robots',
    category: 'seo',
    title: 'Robots (indexace a robots.txt)',
    short: 'Řídí, co smí vyhledávače procházet a indexovat.',
    impact: 'critical',
    difficulty: 'medium',
    effort: '10–20 min',
    what: 'Indexaci řídí dvě věci: soubor robots.txt (co se smí procházet) a meta robots / hlavička X-Robots-Tag (zda stránku indexovat, např. noindex). Snadno se zamění.',
    why: 'Špatné nastavení je jedna z nejničivějších SEO chyb: omylem ponechané „noindex" nebo „Disallow: /" může vyhodit celý web z Googlu. Naopak chybějící pravidla nechají indexovat zbytečné stránky (košík, filtry) a plýtvají crawl budgetem.',
    how: [
      'Zkontroluj, že produkční stránky NEMAJÍ noindex (častý pozůstatek z vývoje/testovacího webu).',
      'V robots.txt neblokuj CSS/JS ani stránky, které chceš indexovat.',
      'Blokuj jen to, co nemá být v indexu (admin, košík, interní vyhledávání).',
      'Odkaz na sitemapu uveď v robots.txt.',
      'Pamatuj: robots.txt brání procházení, ale ne nutně indexaci — na vyřazení z indexu slouží meta noindex.',
    ],
    examples: [
      {
        caption: 'robots.txt',
        good: 'User-agent: *\nDisallow: /admin/\nDisallow: /kosik/\nAllow: /\n\nSitemap: https://www.example.cz/sitemap.xml',
        bad: 'User-agent: *\nDisallow: /        ← zablokuje CELÝ web',
      },
    ],
    conclusion: 'Indexace je „hlavní vypínač" viditelnosti — proto patří mezi kritické kontroly. Po každém nasazení ověř, že nikde nezůstal noindex z testovacího prostředí a že robots.txt neblokuje nic důležitého. Jedna chyba tady umí stát veškerou organickou návštěvnost.',
    related: ['canonical', 'sitemap', 'http'],
  },
  {
    id: 'open_graph',
    category: 'seo',
    title: 'Open Graph (náhledy na sociálních sítích)',
    short: 'Řídí, jak vypadá odkaz sdílený na Facebooku, LinkedInu apod.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '15 min (do šablony)',
    what: 'Open Graph jsou meta tagy (og:title, og:description, og:image, og:url), které určují titulek, popis a obrázek náhledu, když někdo sdílí tvůj odkaz na sociální síti.',
    why: 'Není přímý ranking faktor, ale zásadně ovlivňuje proklik a sdílení ze sociálních sítí. Bez OG tagů si platforma vytáhne náhodný (často ošklivý) náhled, který odrazuje od kliknutí a snižuje dosah obsahu.',
    how: [
      'Doplň og:title, og:description, og:image, og:url a og:type.',
      'Obrázek dělej 1200×630 px (poměr 1,91:1), pod 8 MB, s textem/brandingem.',
      'Hodnoty laď zvlášť pro sdílení — nemusí být identické s title/description.',
      'Otestuj v ladičce Facebooku / LinkedIn Post Inspector.',
    ],
    examples: [
      {
        good: '<meta property="og:title" content="Dětské zimní bundy -50 %">\n<meta property="og:image" content="https://www.example.cz/og/bundy.jpg">\n<meta property="og:description" content="Teplé voděodolné bundy, doprava zdarma.">\n<meta property="og:url" content="https://www.example.cz/bundy">',
      },
    ],
    conclusion: 'Open Graph rozhoduje o tom, jestli sdílený odkaz vypadá profesionálně, nebo odpudivě. Nastav ho jednou do šablony a měj hezký náhledový obrázek — vrátí se ti to v prokliku ze sociálních sítí. Doplň i Twitter Cards pro X/Twitter.',
    related: ['twitter_cards', 'meta_description'],
  },
  {
    id: 'twitter_cards',
    category: 'seo',
    title: 'Twitter Cards',
    short: 'Náhled odkazu na platformě X (Twitter).',
    impact: 'low',
    difficulty: 'easy',
    effort: '5 min',
    what: 'Twitter Cards jsou meta tagy (twitter:card, twitter:title, twitter:image), které určují, jak vypadá tvůj odkaz sdílený na X/Twitteru.',
    why: 'Zvyšují atraktivitu a proklik příspěvků na X. Bez nich se zobrazí jen holý odkaz nebo se použije OG s horší kontrolou nad vzhledem.',
    how: [
      'Nastav twitter:card na „summary_large_image" pro velký náhled.',
      'Doplň twitter:title, twitter:description a twitter:image.',
      'Twitter umí fallbackovat na Open Graph — stačí doplnit jen to, co se liší.',
    ],
    examples: [
      {
        good: '<meta name="twitter:card" content="summary_large_image">\n<meta name="twitter:title" content="Dětské zimní bundy -50 %">\n<meta name="twitter:image" content="https://www.example.cz/og/bundy.jpg">',
      },
    ],
    conclusion: 'Nízká priorita, pokud na X aktivně nesdílíš. Pokud ano, je to pětiminutovka, která zvedne vzhled příspěvků. Často stačí mít kvalitní Open Graph a doplnit jen twitter:card.',
    related: ['open_graph'],
  },
  {
    id: 'structured_data',
    category: 'geo',
    title: 'Strukturovaná data (Schema.org)',
    short: 'Strojově čitelné značky, které odemykají rich results.',
    impact: 'high',
    difficulty: 'medium',
    effort: '30–60 min na typ',
    what: 'Strukturovaná data (nejčastěji JSON-LD podle Schema.org) popisují obsah stránky ve strojově čitelné formě — že jde o produkt, recept, článek, firmu, hodnocení, FAQ apod.',
    why: 'Odemykají „rich results" (hvězdičky hodnocení, ceny, FAQ rozbalovátka, drobečky) přímo ve výsledcích, což výrazně zvedá CTR. Pro AI vyhledávače (GEO) je to klíčový signál, podle kterého obsah chápou a citují.',
    how: [
      'Použij JSON-LD v <script type="application/ld+json"> — Google ho preferuje.',
      'Nasaď typy odpovídající obsahu: Organization a WebSite globálně, dále Product, Article, BreadcrumbList, FAQPage, LocalBusiness.',
      'Vyplň povinné i doporučené vlastnosti; hodnoty musí odpovídat viditelnému obsahu.',
      'Otestuj v Rich Results Test a Schema.org validatoru.',
    ],
    examples: [
      {
        caption: 'JSON-LD — organizace',
        good: '<script type="application/ld+json">\n{\n  "@context": "https://schema.org",\n  "@type": "Organization",\n  "name": "ShopName",\n  "url": "https://www.example.cz",\n  "logo": "https://www.example.cz/logo.png"\n}\n</script>',
      },
    ],
    conclusion: 'Strukturovaná data jsou jeden z nejúčinnějších způsobů, jak vyniknout ve výsledcích a být srozumitelný pro AI. Začni od Organization + WebSite + BreadcrumbList, pak přidej typ podle obsahu (Product/Article/FAQ). Vždy validuj a drž soulad s viditelným obsahem, jinak hrozí ignorování i postih.',
    related: ['geo', 'open_graph', 'headings'],
  },
  {
    id: 'performance',
    category: 'performance',
    title: 'Rychlost načítání',
    short: 'Rychlost je ranking faktor i klíčový prvek UX.',
    impact: 'high',
    difficulty: 'medium',
    effort: 'průběžně',
    what: 'Rychlost zahrnuje dobu odezvy serveru, velikost stránky, počet požadavků a Core Web Vitals (LCP, CLS, INP) — metriky, kterými Google měří reálný zážitek z načítání.',
    why: 'Pomalý web zvyšuje míru opuštění (každá sekunda navíc citelně sráží konverze) a Google rychlost zohledňuje v hodnocení. Na mobilech a pomalém připojení to platí dvojnásob.',
    how: [
      'Změř Core Web Vitals (PageSpeed Insights) a řeš nejdřív LCP a CLS.',
      'Optimalizuj obrázky (WebP/AVIF, komprese, rozměry, lazy loading).',
      'Zapni kompresi (Gzip/Brotli) a cache v prohlížeči.',
      'Odlož nekritické JS (defer/async) a omez blokující CSS.',
      'Zvaž CDN a rychlejší hosting, pokud je vysoké TTFB.',
    ],
    conclusion: 'Rychlost je průřezové téma — detailní jednotlivá doporučení najdeš v nástroji PageSpeed (komprese, blokující zdroje, lazy loading, cache…). Postupuj od největších úspor: obrázky a blokující zdroje obvykle přinesou nejvíc. Cíl: zelené Core Web Vitals na mobilu.',
    related: ['pagespeed', 'compression', 'render-blocking', 'lazy-load-images', 'ttfb'],
  },
  {
    id: 'links',
    category: 'seo',
    title: 'Odkazy (interní a externí)',
    short: 'Funkční odkazy a smysluplné interní prolinkování.',
    impact: 'medium',
    difficulty: 'medium',
    effort: 'průběžně',
    what: 'Interní odkazy spojují stránky tvého webu, externí vedou ven. Vyhledávače po nich procházejí web a předávají jimi „autoritu" (link equity).',
    why: 'Dobré interní prolinkování pomáhá Googlu objevit a pochopit důležitost stránek a drží návštěvníky déle na webu. Nefunkční odkazy (404) kazí zážitek i crawl. Odkazy bez popisného textu („klikněte zde") nepředávají kontext.',
    how: [
      'Prolinkuj související obsah popisným kotevním textem (ne „zde"/„více").',
      'Z důležitých stránek odkazuj na další klíčové stránky (rozkládej autoritu).',
      'Najdi a oprav nefunkční odkazy (nástroj broken-links).',
      'Externí odkazy na nedůvěryhodné/placené cíle označ rel="nofollow"/"sponsored".',
    ],
    conclusion: 'Interní odkazy jsou podceňovaný, ale mocný nástroj — řídíš jimi, co Google považuje za důležité. Udržuj je funkční a popisné a vědomě prolinkovávej pilíře obsahu. Pravidelně kontroluj broken links, ať neztrácíš autoritu ani návštěvníky.',
    related: ['broken-links', 'url_structure', 'headings'],
  },
  {
    id: 'favicon',
    category: 'seo',
    title: 'Favicon',
    short: 'Ikona webu v záložce a ve výsledcích na mobilu.',
    impact: 'low',
    difficulty: 'easy',
    effort: '10 min',
    what: 'Favicon je malá ikona webu zobrazená v záložce prohlížeče, v historii a u mobilních výsledků vyhledávání vedle názvu webu.',
    why: 'Posiluje rozpoznatelnost značky a důvěryhodnost. Google ji ukazuje u mobilních výsledků, takže přispívá k prokliku. Chybějící favicon působí nedodělaně.',
    how: [
      'Připrav favicon ve více velikostech (16, 32, 180, 192, 512 px) a v moderním formátu.',
      'Odkaz vlož do <head> (icon, apple-touch-icon).',
      'Musí být dostupný na stabilní URL a vracet 200.',
    ],
    examples: [
      { good: '<link rel="icon" href="/favicon.ico" sizes="any">\n<link rel="icon" type="image/svg+xml" href="/icon.svg">\n<link rel="apple-touch-icon" href="/apple-touch-icon.png">' },
    ],
    conclusion: 'Malý detail, který dotváří profesionální dojem a rozpoznatelnost ve výsledcích na mobilu. Nasaď kompletní sadu velikostí včetně apple-touch-icon a ověř dostupnost. Jednorázová záležitost na deset minut.',
    related: ['favicon'],
  },
  {
    id: 'url_structure',
    category: 'seo',
    title: 'Struktura URL',
    short: 'Krátké, čitelné a logické adresy s klíčovými slovy.',
    impact: 'medium',
    difficulty: 'hard',
    effort: 'náročné u existujícího webu (přesměrování)',
    what: 'Struktura URL je podoba adres stránek — jejich délka, čitelnost, použití slov vs. parametrů a logika složek (/kategorie/produkt).',
    why: 'Čisté URL s klíčovými slovy pomáhají uživatelům i vyhledávačům pochopit, o čem stránka je, a lépe se sdílejí a proklikávají. Dlouhé adresy plné parametrů a ID jsou hůř zapamatovatelné a mohou tvořit duplicity.',
    how: [
      'Používej slova oddělená pomlčkami, malými písmeny, bez diakritiky a zbytečných parametrů.',
      'Drž logickou hierarchii složek odpovídající navigaci.',
      'URL drž krátké a stabilní — neměň je zbytečně.',
      'Při nutné změně vždy nastav 301 přesměrování ze staré na novou adresu.',
    ],
    examples: [
      {
        bad: 'example.cz/index.php?cat=12&id=8842&sort=asc',
        good: 'example.cz/zimni-bundy/detska-bunda-modra',
      },
    ],
    conclusion: 'U nového webu navrhni čisté URL hned správně — zpětné změny jsou náročné a riskantní (nutnost přesměrování). U existujícího měň URL jen s jasným přínosem a vždy s 301. Čitelná adresa je drobná, ale trvalá výhoda pro SEO i uživatele.',
    related: ['canonical', 'redirects', 'links'],
  },
  {
    id: 'content_quality',
    category: 'seo',
    title: 'Kvalita a délka obsahu',
    short: 'Dostatečně obsáhlý, čitelný a unikátní obsah.',
    impact: 'high',
    difficulty: 'hard',
    effort: 'náročné (psaní obsahu)',
    what: 'Kvalita obsahu zahrnuje délku (počet slov), čitelnost, strukturu, unikátnost a to, jak dobře text odpovídá na záměr hledajícího.',
    why: 'Google odměňuje obsah, který nejlépe a nejúplněji odpovídá na dotaz (helpful content). Tenký, duplicitní nebo špatně čitelný obsah se rankuje hůř. Délka sama o sobě není cíl, ale příliš krátký text obvykle nepokryje téma do hloubky.',
    how: [
      'Pokryj téma komplexně — odpověz na otázky, které k němu uživatelé mají (orientačně 500+ slov u běžných stránek, víc u pilířových).',
      'Piš čitelně: kratší věty, odstavce, mezititulky, odrážky.',
      'Zajisti unikátnost — žádné kopírování ani opakování stejného textu napříč stránkami.',
      'Doplň hodnotu navíc: data, příklady, FAQ, vizuály.',
      'Hlavní klíčové slovo a jeho varianty používej přirozeně (vyhni se přeoptimalizaci).',
    ],
    conclusion: 'Obsah je dlouhodobě nejsilnější páka SEO — žádná technická úprava nenahradí text, který nejlíp odpoví na dotaz. Cíl není „víc slov", ale „úplná a čitelná odpověď na záměr hledajícího". Investice do obsahu je náročná, ale vrací se nejvíc.',
    related: ['headings', 'title', 'links'],
  },
]

// ─────────────────────────────────────────────────────────────────────────
// PAGESPEED / RYCHLOST — id shodná s opportunities/diagnostics v ToolsService
// ─────────────────────────────────────────────────────────────────────────

const PERF_ENTRIES: KbEntry[] = [
  {
    id: 'defer-js',
    category: 'performance',
    title: 'Defer / async na skriptech',
    short: 'Synchronní <script> blokuje vykreslení — odlož ho.',
    impact: 'high',
    difficulty: 'easy',
    effort: '5–15 min',
    what: 'Když prohlížeč narazí na obyčejný <script>, zastaví vykreslování stránky, skript stáhne a spustí, a teprve pak pokračuje. Tomu se říká „render-blocking" (blokující) skript. Atributy defer a async tohle chování mění.',
    why: 'Každý blokující skript v hlavičce oddaluje okamžik, kdy uživatel uvidí obsah (zhoršuje LCP a First Paint). U více skriptů se zpoždění sčítá. Na mobilu a pomalém připojení to znamená vteřiny prázdné bílé stránky — a vyšší míru opuštění.',
    how: [
      'Skripty, které nejsou potřeba pro první vykreslení, označ atributem defer — stáhnou se na pozadí a spustí se až po sestavení stránky, v pořadí, v jakém jsou.',
      'Pro nezávislé skripty (analytika, chat) použij async — spustí se hned po stažení, bez ohledu na pořadí.',
      'Kritické skripty drž minimální; zbytek načítej na konci <body> nebo s defer.',
      'Pravidlo: defer = závisí na DOM nebo na pořadí; async = samostatný, na ničem nezáleží.',
    ],
    examples: [
      {
        bad: '<head>\n  <script src="/js/app.js"></script>   <!-- blokuje vykreslení -->\n</head>',
        good: '<head>\n  <script src="/js/app.js" defer></script>\n  <script src="/js/analytics.js" async></script>\n</head>',
      },
    ],
    conclusion: 'Defer/async je jedna z nejlevnějších a nejúčinnějších optimalizací rychlosti — často stačí dopsat jedno slovo k tagu. Pro většinu skriptů je správná volba defer. Po nasazení ověř, že nic nepřestalo fungovat (hlavně skripty závislé na pořadí) a změř zlepšení LCP.',
    related: ['render-blocking', 'reduce-blocking-css', 'inline-scripts', 'performance'],
  },
  {
    id: 'render-blocking',
    category: 'performance',
    title: 'Blokující zdroje (render-blocking)',
    short: 'CSS a JS v hlavičce, které brzdí první vykreslení.',
    impact: 'high',
    difficulty: 'medium',
    effort: '30–60 min',
    what: 'Render-blocking zdroje jsou soubory (typicky CSS a synchronní JS v <head>), které musí prohlížeč stáhnout a zpracovat dřív, než vykreslí obsah. Do té doby vidí uživatel prázdno.',
    why: 'Jsou nejčastější příčinou pomalého prvního vykreslení a špatného LCP. Čím víc jich je a čím větší jsou, tím déle trvá, než se objeví obsah.',
    how: [
      'Skripty odlož přes defer/async.',
      'Identifikuj „critical CSS" (styly pro viditelnou část) a vlož je inline, zbytek načti asynchronně.',
      'Sluč a minifikuj CSS/JS soubory, odstraň nepoužívané styly.',
      'Externí fonty načítej s font-display: swap a přednačti je.',
    ],
    conclusion: 'Cílem je doručit viditelný obsah co nejdřív a vše ostatní odložit. Začni odložením skriptů (rychlá výhra) a optimalizací CSS (critical CSS). Tahle oblast má přímý dopad na LCP — metriku, kterou Google sleduje.',
    related: ['defer-js', 'reduce-blocking-css', 'reduce-css-files', 'performance'],
  },
  {
    id: 'reduce-blocking-css',
    category: 'performance',
    title: 'Omezení blokujícího CSS',
    short: 'Velké CSS v hlavičce zdržuje vykreslení.',
    impact: 'high',
    difficulty: 'hard',
    effort: '1–3 h',
    what: 'CSS je ve výchozím stavu blokující — prohlížeč nevykreslí nic, dokud nestáhne a nezpracuje styly. Velké nebo četné CSS soubory tak oddalují první obraz stránky.',
    why: 'Načítání jednoho velkého CSS pro celý web zdržuje i jednoduchou stránku. Optimalizace přímo zlepšuje LCP a vnímanou rychlost.',
    how: [
      'Vyextrahuj „critical CSS" pro viditelnou část a vlož ho inline do <head>.',
      'Zbytek CSS načti asynchronně (media="print" trik nebo rel="preload" + onload).',
      'Odstraň nepoužívané styly (PurgeCSS / nástroj Coverage v devtools).',
      'Rozděl CSS po stránkách/komponentách místo jednoho monolitu.',
    ],
    examples: [
      {
        caption: 'Asynchronní načtení nekritického CSS',
        good: '<link rel="preload" href="/css/styly.css" as="style" onload="this.rel=\'stylesheet\'">\n<noscript><link rel="stylesheet" href="/css/styly.css"></noscript>',
      },
    ],
    conclusion: 'Critical CSS inline + zbytek asynchronně je nejúčinnější vzorec proti blokujícímu CSS. Je to náročnější úprava, ale u pomalých webů přináší výrazné zrychlení prvního vykreslení. Ulehčí ti ji moderní build nástroje a frameworky.',
    related: ['render-blocking', 'reduce-css-files', 'inline-styles', 'performance'],
  },
  {
    id: 'compression',
    category: 'performance',
    title: 'Komprese odpovědí (Gzip/Brotli)',
    short: 'Bez komprese se přenáší zbytečně velký objem dat.',
    impact: 'high',
    difficulty: 'easy',
    effort: '10–20 min',
    what: 'Komprese (Gzip nebo modernější Brotli) zmenší textové soubory (HTML, CSS, JS, SVG, JSON) před odesláním po síti — klidně o 60–80 %. Prohlížeč si je sám rozbalí.',
    why: 'Bez komprese se přenáší mnohonásobně víc dat, což zpomaluje načítání hlavně na mobilech a pomalém připojení. Zapnutí je jedna z nejvýhodnějších optimalizací — minimum práce, velký efekt.',
    how: [
      'Zapni Brotli (ideálně) nebo Gzip na webserveru / CDN.',
      'Komprimuj textové typy: HTML, CSS, JS, SVG, JSON, XML.',
      'Obrázky a videa nekomprimuj Gzipem (už jsou komprimované) — řeš je formátem.',
      'Ověř hlavičku odpovědi „Content-Encoding: br/gzip".',
    ],
    examples: [
      {
        caption: 'Gzip v .htaccess (Apache, mod_deflate)',
        good: '<IfModule mod_deflate.c>\n  AddOutputFilterByType DEFLATE text/html text/css application/javascript application/json image/svg+xml\n</IfModule>',
      },
    ],
    conclusion: 'Komprese je klasická „rychlá výhra" — pár řádků konfigurace nebo přepínač u hostingu ušetří desítky procent přenášených dat. Pokud běží, ověř, že pokrývá všechny textové typy; ideálem je Brotli. Tohle by mělo být zapnuté vždy.',
    related: ['enable-compression', 'page-size', 'performance'],
  },
  {
    id: 'enable-compression',
    category: 'performance',
    title: 'Zapnutí komprese',
    short: 'Komprese není aktivní — zapni Gzip/Brotli.',
    impact: 'high',
    difficulty: 'easy',
    effort: '10–20 min',
    what: 'Detekovali jsme, že server odpovědi nekomprimuje. Zapnutím Gzip/Brotli se textové soubory přenášejí výrazně menší.',
    why: 'Vypnutá komprese je zbytečná ztráta rychlosti i dat. Patří mezi první věci, které řešit — efekt je okamžitý a plošný.',
    how: [
      'U sdíleného hostingu hledej přepínač „Gzip/komprese", často stačí zapnout.',
      'Na vlastním serveru zapni mod_deflate/mod_brotli (Apache) nebo gzip/brotli (Nginx).',
      'Přes CDN (Cloudflare apod.) lze kompresi zapnout centrálně.',
      'Po zapnutí ověř Content-Encoding v odpovědi.',
    ],
    conclusion: 'Viz téma „Komprese odpovědí" — princip je stejný, jen tady ještě není zapnutá. Je to jedna z nejjednodušších a nejvýhodnějších úprav vůbec; nasaď ji jako první.',
    related: ['compression', 'page-size', 'performance'],
  },
  {
    id: 'browser-caching',
    category: 'performance',
    title: 'Cache v prohlížeči',
    short: 'Statické soubory se mají ukládat u návštěvníka.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '15–30 min',
    what: 'Cache v prohlížeči (přes hlavičky Cache-Control / Expires) umožní, aby si prohlížeč uložil statické soubory (obrázky, CSS, JS, fonty) a při další návštěvě je nestahoval znovu.',
    why: 'Bez cache stahuje vracející se návštěvník vše znovu při každém načtení — zbytečně pomalé a datově náročné. Správná cache výrazně zrychlí opakované návštěvy a odlehčí serveru.',
    how: [
      'Nastav dlouhou cache (např. 1 rok) pro neměnné statické soubory.',
      'Používej „cache busting" — verzování v názvu (app.4f3a.js), ať při změně dostane uživatel novou verzi.',
      'HTML drž s krátkou nebo žádnou cache (musí být aktuální).',
      'Ověř hlavičky Cache-Control v odpovědi.',
    ],
    examples: [
      {
        caption: 'Cache statických souborů (.htaccess)',
        good: '<IfModule mod_expires.c>\n  ExpiresActive On\n  ExpiresByType image/webp "access plus 1 year"\n  ExpiresByType text/css "access plus 1 year"\n  ExpiresByType application/javascript "access plus 1 year"\n</IfModule>',
      },
    ],
    conclusion: 'Cache v prohlížeči je levný způsob, jak zrychlit opakované návštěvy. Klíč je kombinace dlouhé cache pro statiku + verzování názvů, abys mohl soubory měnit bez rizika, že uvidí starou verzi. HTML naopak nech čerstvé.',
    related: ['compression', 'performance'],
  },
  {
    id: 'lazy-load-images',
    category: 'performance',
    title: 'Lazy loading obrázků',
    short: 'Obrázky mimo obrazovku načítej až při scrollování.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '10–30 min',
    what: 'Lazy loading znamená, že obrázky mimo viditelnou oblast se nenačtou hned, ale teprve když k nim uživatel doscrolluje. Stačí atribut loading="lazy".',
    why: 'Bez lazy loadingu stahuje prohlížeč i obrázky úplně dole na stránce, které uživatel třeba nikdy neuvidí — zdržuje to první vykreslení a plýtvá daty. Lazy loading zrychlí start a šetří přenos.',
    how: [
      'Přidej loading="lazy" obrázkům a iframe pod viditelnou oblastí.',
      'NEpoužívej lazy na hlavní obrázek nad ohybem (LCP obrázek) — ten chceš naopak co nejdřív (klidně preload).',
      'Vždy uváděj width a height, ať se layout neposkakuje (CLS).',
    ],
    examples: [
      { good: '<img src="galerie-12.webp" alt="..." loading="lazy" width="800" height="600">' },
    ],
    conclusion: 'Lazy loading je dnes nativní (jeden atribut) a téměř zadarmo zrychlí stránky s množstvím obrázků. Pozor jen na hlavní obrázek nad ohybem — ten lazy nedávej. Doplň rozměry kvůli stabilitě layoutu.',
    related: ['images', 'modern-image-formats', 'img-dimensions', 'performance'],
  },
  {
    id: 'modern-image-formats',
    category: 'performance',
    title: 'Moderní formáty obrázků (WebP/AVIF)',
    short: 'WebP a AVIF jsou o 25–50 % menší než JPEG/PNG.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '20–40 min',
    what: 'WebP a AVIF jsou moderní obrazové formáty s výrazně lepší kompresí než JPEG/PNG při stejné kvalitě. Podporují je všechny současné prohlížeče.',
    why: 'Obrázky bývají největší část váhy stránky. Přechod na WebP/AVIF ušetří desítky procent dat bez viditelné ztráty kvality — přímé zrychlení.',
    how: [
      'Převeď obrázky do WebP (univerzální) nebo AVIF (ještě menší).',
      'Použij <picture> s fallbackem pro jistotu.',
      'Automatizuj konverzi v buildu / přes CDN s transformací obrázků.',
      'Zároveň zmenši na reálně potřebné rozměry.',
    ],
    examples: [
      {
        good: '<picture>\n  <source srcset="foto.avif" type="image/avif">\n  <source srcset="foto.webp" type="image/webp">\n  <img src="foto.jpg" alt="..." width="800" height="600">\n</picture>',
      },
    ],
    conclusion: 'Moderní formáty jsou jedna z nejúčinnějších cest, jak snížit váhu stránky. Ideálně automatizuj konverzi, ať to není ruční práce u každého obrázku. Kombinuj s kompresí, správnými rozměry a lazy loadingem.',
    related: ['images', 'lazy-load-images', 'page-size', 'performance'],
  },
  {
    id: 'ttfb',
    category: 'performance',
    title: 'TTFB — odezva serveru',
    short: 'Doba, než server pošle první byte odpovědi.',
    impact: 'high',
    difficulty: 'hard',
    effort: 'dle příčiny',
    what: 'TTFB (Time To First Byte) je doba od požadavku do chvíle, kdy přijde první byte odpovědi serveru. Zahrnuje práci serveru (databáze, generování HTML) a síťovou latenci.',
    why: 'Vysoké TTFB zdržuje úplně všechno ostatní — stránka se nemůže začít vykreslovat dřív, než dorazí HTML. Bývá to příznak pomalého hostingu, neefektivní aplikace nebo chybějící cache.',
    how: [
      'Zapni serverovou cache (cache stránek / objektová cache), ať se HTML negeneruje při každém požadavku.',
      'Optimalizuj pomalé databázové dotazy a aplikační kód.',
      'Nasaď CDN, ať je obsah blíž k uživateli.',
      'Zvaž rychlejší/výkonnější hosting, pokud je server přetížený.',
    ],
    conclusion: 'TTFB je „startovní čára" — pokud je vysoké, žádná front-end optimalizace ho nezachrání. Nejčastější lék je cache (serverová + CDN) a rychlejší backend. Cíl je držet TTFB nízko (orientačně do ~0,5–0,8 s) na mobilu.',
    related: ['browser-caching', 'performance', 'http-version'],
  },
  {
    id: 'page-size',
    category: 'performance',
    title: 'Celková váha stránky',
    short: 'Příliš velká stránka se načítá pomalu.',
    impact: 'high',
    difficulty: 'medium',
    effort: 'průběžně',
    what: 'Váha stránky je součet všech stažených zdrojů — HTML, CSS, JS, obrázky, fonty, videa. Čím větší, tím déle a víc dat se přenáší.',
    why: 'Těžké stránky se načítají pomalu hlavně na mobilech a omezených datech. Velká část váhy bývá v neoptimalizovaných obrázcích a objemném JavaScriptu.',
    how: [
      'Optimalizuj obrázky (formát, komprese, rozměry, lazy loading) — obvykle největší úspora.',
      'Zmenši a sluč CSS/JS, odstraň nepoužívaný kód.',
      'Zapni kompresi a cache.',
      'Omez těžké externí skripty a embedy (widgety, mapy, videa) — načítej je až na vyžádání.',
    ],
    conclusion: 'Váhu stránky řeš odshora dolů: nejdřív obrázky a JavaScript (největší díly), pak komprese a cache. Lehčí stránka = rychlejší načtení, lepší Core Web Vitals a nižší míra opuštění, zvlášť na mobilu.',
    related: ['modern-image-formats', 'compression', 'reduce-js-files', 'performance'],
  },
  {
    id: 'reduce-page-size',
    category: 'performance',
    title: 'Snížení váhy stránky',
    short: 'Stránka je velká — odlehči ji.',
    impact: 'high',
    difficulty: 'medium',
    effort: 'průběžně',
    what: 'Stránka přesahuje doporučenou váhu. Cílem je zmenšit objem přenášených dat při zachování obsahu.',
    why: 'Stejné jako u celkové váhy — těžká stránka zpomaluje načítání a zhoršuje UX i pozice na mobilu.',
    how: [
      'Začni u největších zdrojů (zpravidla obrázky a JS).',
      'Převeď obrázky na WebP/AVIF a zmenši rozměry.',
      'Odeber nepoužívané knihovny a kód, rozděl JS (code splitting).',
      'Zapni kompresi a dlouhou cache statiky.',
    ],
    conclusion: 'Postupuj podle principu „největší úspora první". Měř váhu po každém kroku, ať vidíš, co reálně pomohlo. Viz i obecné téma „Celková váha stránky".',
    related: ['page-size', 'modern-image-formats', 'reduce-js-files', 'performance'],
  },
  {
    id: 'requests',
    category: 'performance',
    title: 'Počet HTTP požadavků',
    short: 'Mnoho souborů = mnoho požadavků = pomalejší start.',
    impact: 'medium',
    difficulty: 'medium',
    effort: '30–60 min',
    what: 'Každý soubor (CSS, JS, obrázek, font) je samostatný HTTP požadavek. Velké množství malých požadavků zvyšuje režii, zvlášť na pomalejším spojení.',
    why: 'Hodně požadavků znamená víc navazování spojení a režie. Na HTTP/2/3 je to méně bolestivé, ale slučování a odstranění zbytečností pořád pomáhá.',
    how: [
      'Sluč drobné CSS/JS soubory (build bundler).',
      'Používej CSS sprity nebo inline SVG pro malé ikony.',
      'Odeber nepoužívané knihovny a externí skripty.',
      'Přejdi na HTTP/2 nebo HTTP/3, které paralelní požadavky zvládají lépe.',
    ],
    conclusion: 'Méně a větších souborů obvykle poráží mnoho malých. Sluč, co jde, odeber zbytečnosti a zapni HTTP/2+. Spolu s kompresí a cache to tvoří základ rychlého doručení.',
    related: ['reduce-css-files', 'reduce-js-files', 'http-version', 'performance'],
  },
  {
    id: 'reduce-css-files',
    category: 'performance',
    title: 'Méně CSS souborů',
    short: 'Sluč a minifikuj styly.',
    impact: 'low',
    difficulty: 'easy',
    effort: '20–40 min',
    what: 'Mnoho samostatných CSS souborů znamená víc požadavků a často i duplicity. Slučování a minifikace je zmenší a sjednotí.',
    why: 'Méně a menší CSS = rychlejší stažení a zpracování. Minifikace navíc odstraní mezery a komentáře.',
    how: [
      'Sluč CSS do co nejmenšího počtu souborů (build nástroj).',
      'Minifikuj (odstranění mezer, komentářů).',
      'Odstraň nepoužívané styly.',
    ],
    conclusion: 'Drobná, snadná optimalizace, kterou většinou vyřeší build nástroj automaticky. Kombinuj se snižováním blokujícího CSS pro maximální efekt.',
    related: ['reduce-blocking-css', 'requests', 'performance'],
  },
  {
    id: 'reduce-js-files',
    category: 'performance',
    title: 'Méně a menší JavaScript',
    short: 'Velký JS zdržuje a zatěžuje procesor zařízení.',
    impact: 'medium',
    difficulty: 'medium',
    effort: '1–2 h',
    what: 'JavaScript se musí stáhnout, naparsovat a spustit — to vše stojí čas a výkon, hlavně na slabších mobilech. Velké JS balíky zhoršují interaktivitu (INP).',
    why: 'JS je „nejdražší" typ zdroje — na rozdíl od obrázku ho zařízení musí i zpracovat. Nadbytečné knihovny a nepoužívaný kód zbytečně zatěžují.',
    how: [
      'Odstraň nepoužívané knihovny a kód (tree-shaking).',
      'Rozděl JS po stránkách (code splitting), načítej až co je potřeba.',
      'Odlož nekritické skripty (defer/async).',
      'Zvaž lehčí alternativy k velkým knihovnám.',
    ],
    conclusion: 'JavaScript drž na minimu — každý kilobyte se „platí" dvakrát (přenos + zpracování). Code splitting a odstranění mrtvého kódu nejvíc pomohou interaktivitě (INP). Tohle je často největší rezerva u moderních webů.',
    related: ['defer-js', 'requests', 'inline-scripts', 'performance'],
  },
  {
    id: 'dom-size',
    category: 'performance',
    title: 'Velikost DOM',
    short: 'Příliš mnoho HTML elementů zpomaluje vykreslování.',
    impact: 'medium',
    difficulty: 'medium',
    effort: 'dle příčiny',
    what: 'DOM je strom HTML elementů stránky. Příliš velký nebo hluboce zanořený DOM (tisíce uzlů) zpomaluje vykreslování, layout a JavaScriptové operace.',
    why: 'Velký DOM zvyšuje spotřebu paměti a procesoru, zhoršuje interaktivitu a plynulost — hlavně na mobilech. Bývá příznakem nabobtnalých šablon nebo nadbytečných obalů.',
    how: [
      'Zjednoduš strukturu — odeber zbytečné <div> obaly.',
      'U dlouhých seznamů/tabulek použij stránkování nebo virtualizaci.',
      'Nenač­ítej skrytý obsah, který není potřeba (renderuj až na vyžádání).',
    ],
    conclusion: 'Štíhlý DOM se rychleji vykresluje i ovládá. Orientačně drž počet uzlů v řádu nižších tisíců a vyhýbej se hlubokému zanoření. U datově bohatých stránek pomáhá virtualizace seznamů.',
    related: ['excessive-dom', 'reduce-js-files', 'performance'],
  },
  {
    id: 'excessive-dom',
    category: 'performance',
    title: 'Nadměrný DOM',
    short: 'Počet elementů překračuje rozumnou mez.',
    impact: 'medium',
    difficulty: 'medium',
    effort: 'dle příčiny',
    what: 'Stránka má nadměrné množství HTML elementů. To zatěžuje paměť i procesor a zpomaluje vykreslení a interakce.',
    why: 'Stejné jako u velikosti DOM — víc uzlů znamená víc práce pro prohlížeč při každé změně a horší plynulost na slabších zařízeních.',
    how: [
      'Zredukuj zbytečné obalové elementy a vnoření.',
      'Stránkuj/virtualizuj dlouhé seznamy a tabulky.',
      'Zjednoduš složité komponenty a šablony.',
    ],
    conclusion: 'Viz téma „Velikost DOM". Příčinou bývají nabobtnalé šablony nebo dlouhé seznamy bez stránkování — řeš to nejdřív, přinese to největší úlevu.',
    related: ['dom-size', 'performance'],
  },
  {
    id: 'inline-styles',
    category: 'performance',
    title: 'Inline styly',
    short: 'Hodně style="..." zhoršuje cache a údržbu.',
    impact: 'low',
    difficulty: 'easy',
    effort: '30–60 min',
    what: 'Inline styly jsou CSS zapsané přímo v atributu style="..." u elementů. Ve velkém množství zvětšují HTML a nedají se cachovat ani znovu použít.',
    why: 'HTML s množstvím inline stylů je větší (přenáší se při každém načtení) a hůř se udržuje. Výjimkou je záměrné inline „critical CSS" v hlavičce kvůli rychlosti.',
    how: [
      'Přesuň opakované styly do externího/sloučeného CSS s třídami.',
      'Inline ponech jen pro skutečně jedinečné, dynamicky počítané hodnoty.',
      'Záměrné critical CSS v <head> je v pořádku — to není totéž.',
    ],
    conclusion: 'Nízká priorita, spíš otázka čistoty a cachovatelnosti. Hromadné inline styly přesuň do tříd; výjimkou je vědomé critical CSS. Neřeš dřív než váhu obrázků a blokující zdroje.',
    related: ['reduce-blocking-css', 'reduce-css-files', 'performance'],
  },
  {
    id: 'inline-scripts',
    category: 'performance',
    title: 'Inline skripty',
    short: 'Mnoho <script> přímo v HTML blokuje a nelze cachovat.',
    impact: 'low',
    difficulty: 'medium',
    effort: '30–60 min',
    what: 'Inline skripty jsou kód přímo v <script>…</script> v HTML (ne externí soubor). Blokují vykreslení v místě výskytu a nedají se cachovat ani odložit přes defer/async.',
    why: 'Velké inline skripty nafukují HTML a blokují vykreslování. Navíc komplikují bezpečnostní politiku (CSP).',
    how: [
      'Přesuň kód do externích .js souborů a načti je s defer.',
      'Malé nezbytné inicializace ponech, ale drž je minimální.',
      'Zvaž CSP — inline skripty ji komplikují.',
    ],
    conclusion: 'Externí soubory s defer obvykle porazí inline skripty (cachovatelnost + neblokují). Velké inline bloky přesuň ven; drobné inicializace jsou v pořádku. Souvisí s celkovou strategií odkládání JS.',
    related: ['defer-js', 'reduce-js-files', 'performance'],
  },
  {
    id: 'img-dimensions',
    category: 'performance',
    title: 'Rozměry obrázků (width/height)',
    short: 'Bez rozměrů obrázku poskakuje layout (CLS).',
    impact: 'medium',
    difficulty: 'easy',
    effort: '5–15 min',
    what: 'Když <img> nemá uvedené width a height, prohlížeč do načtení neví, kolik místa obrázek zabere — a po jeho dorazení obsah „poskočí". To měří metrika CLS (Cumulative Layout Shift).',
    why: 'Poskakování layoutu je frustrující (uživatel klikne na něco jiného, než chtěl) a Google ho penalizuje přes CLS v Core Web Vitals.',
    how: [
      'Uváděj width a height u všech <img> (prohlížeč si z nich spočítá poměr stran).',
      'Případně rezervuj místo přes aspect-ratio v CSS.',
      'Totéž platí pro embedy a reklamy — rezervuj jim prostor.',
    ],
    examples: [
      { bad: '<img src="foto.webp" alt="...">', good: '<img src="foto.webp" alt="..." width="800" height="600">' },
    ],
    conclusion: 'Doplnit rozměry obrázků je pětiminutovka s přímým dopadem na CLS — jednu z Core Web Vitals. Kombinuj s lazy loadingem a moderními formáty. Rezervuj místo i embedům a reklamám, ať nic neposkakuje.',
    related: ['images', 'lazy-load-images', 'performance'],
  },
  {
    id: 'no-preload',
    category: 'performance',
    title: 'Přednačtení klíčových zdrojů (preload)',
    short: 'Důležité zdroje lze načíst dřív přes rel="preload".',
    impact: 'low',
    difficulty: 'medium',
    effort: '15–30 min',
    what: 'rel="preload" říká prohlížeči, ať s vysokou prioritou předem stáhne zdroj, který bude brzy potřeba (hlavní obrázek nad ohybem, klíčový font, kritické CSS).',
    why: 'Bez preloadu prohlížeč objeví některé důležité zdroje pozdě (např. font odkazovaný až z CSS), což oddálí jejich projevení. Preload to urychlí a zlepší LCP.',
    how: [
      'Přednačti LCP obrázek (hlavní vizuál nad ohybem).',
      'Přednačti klíčové fonty a přidej font-display: swap.',
      'Nepřeháněj to — preload všeho prioritu rozmělní a uškodí.',
    ],
    examples: [
      { good: '<link rel="preload" href="/fonts/jakarta.woff2" as="font" type="font/woff2" crossorigin>\n<link rel="preload" href="/img/hero.webp" as="image">' },
    ],
    conclusion: 'Preload je cílený nástroj pro pár opravdu klíčových zdrojů (LCP obrázek, hlavní font). Použitý s rozmyslem zlepší LCP; použitý plošně naopak uškodí. Méně je víc.',
    related: ['render-blocking', 'performance'],
  },
  {
    id: 'http-version',
    category: 'performance',
    title: 'Verze protokolu HTTP',
    short: 'HTTP/2 a HTTP/3 jsou rychlejší než staré HTTP/1.1.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '10–30 min (často u hostingu/CDN)',
    what: 'HTTP/2 a HTTP/3 jsou novější verze protokolu, které umí paralelní přenos více souborů přes jedno spojení, kompresi hlaviček a (HTTP/3) odolnost vůči ztrátě paketů.',
    why: 'Na starém HTTP/1.1 se soubory přenášejí prakticky po jednom, což brzdí stránky s více zdroji. Přechod na HTTP/2+ zrychlí načítání bez zásahu do kódu.',
    how: [
      'Ověř u hostingu/CDN podporu HTTP/2 a HTTP/3 a zapni je.',
      'Vyžaduje HTTPS (další důvod mít certifikát).',
      'Na HTTP/2 přestává dávat smysl agresivní slučování — drobnější soubory jsou OK.',
    ],
    conclusion: 'Upgrade protokolu je obvykle bezbolestná výhra — často stačí zapnout u hostingu nebo nasadit CDN. Vyžaduje HTTPS. Zrychlí hlavně weby s větším počtem zdrojů.',
    related: ['https', 'requests', 'ttfb', 'performance'],
  },
]

// ─────────────────────────────────────────────────────────────────────────
// UX / UI AUDIT — id shodná s checks v checkUx()
// ─────────────────────────────────────────────────────────────────────────

const UX_ENTRIES: KbEntry[] = [
  {
    id: 'zoom',
    category: 'ux',
    title: 'Blokovaný zoom stránky',
    short: 'Nikdy nezakazujte uživateli přiblížení stránky.',
    impact: 'high',
    difficulty: 'easy',
    effort: '2 min',
    what: 'Některé weby ve viewport metě zakážou přibližování přes user-scalable=no nebo maximum-scale=1. Uživatel pak nemůže stránku „roztáhnout" prsty.',
    why: 'Zákaz zoomu je vážný přístupnostní problém — lidé se zhoršeným zrakem si potřebují obsah přiblížit. Je to porušení WCAG a na mobilech přímo škodí použitelnosti. Žádnou reálnou výhodu to nepřináší.',
    how: [
      'Z viewport meta tagu odstraňte user-scalable=no i maximum-scale.',
      'Ponechte jen "width=device-width, initial-scale=1".',
      'Pokud se zoom „rozbíjel" layout, vyřešte to responzivním CSS, ne zákazem zoomu.',
    ],
    examples: [
      {
        bad: '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no">',
        good: '<meta name="viewport" content="width=device-width, initial-scale=1">',
      },
    ],
    conclusion: 'Zoom nikdy nezakazujte — je to dvouminutová oprava s přímým dopadem na přístupnost. Odeberte user-scalable=no a maximum-scale a nechte uživatele přibližovat. Patří to mezi rychlé a důležité UX výhry.',
    related: ['viewport', 'tiny_fonts', 'mobile'],
  },
  {
    id: 'tiny_fonts',
    category: 'ux',
    title: 'Příliš malé písmo',
    short: 'Základní text alespoň 16px, drobný minimálně 12px.',
    impact: 'high',
    difficulty: 'easy',
    effort: '15–30 min',
    what: 'Velikost písma rozhoduje o čitelnosti. Text menší než zhruba 12px je na mobilu obtížně čitelný a nutí uživatele přibližovat nebo mhouřit oči.',
    why: 'Špatně čitelný text zvyšuje únavu a míru opuštění a poškozuje přístupnost. Google v testu mobilní použitelnosti hlásí „text je příliš malý". Pohodlné čtení je základ dobrého UX.',
    how: [
      'Základní text webu nastavte na 16px (1rem) nebo víc.',
      'Drobný text (popisky, poznámky) držte minimálně na 12px.',
      'Velikosti laďte relativně (rem) od základní velikosti, ne pevně po pixelech.',
      'Otestujte čitelnost na reálném telefonu, ne jen na velkém monitoru.',
    ],
    examples: [
      { caption: 'Základní velikost písma', good: 'body { font-size: 16px; line-height: 1.6; }\n.small { font-size: 0.875rem; } /* 14px */' },
    ],
    conclusion: 'Čitelnost je základ — 16px pro běžný text je dnešní standard, pod 12px nejdete. Pracujte s rem od základní velikosti, ať vše škáluje konzistentně. Levná úprava s velkým dopadem na komfort čtení.',
    related: ['clickable_fonts', 'relative_units', 'content_quality'],
  },
  {
    id: 'clickable_fonts',
    category: 'ux',
    title: 'Čitelnost a velikost odkazů/tlačítek',
    short: 'Klikací prvky musí být čitelné a snadno trefitelné.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '15–30 min',
    what: 'Odkazy a tlačítka s drobným písmem se hůř čtou a hlavně se na dotykovém displeji hůř trefují prstem. Souvisí to s velikostí tzv. tap-targetu (klikací plochy).',
    why: 'Malé klikací prvky zvyšují chybovost — uživatel místo kliknutí mine nebo trefí sousední odkaz. To frustruje a snižuje konverze. Google doporučuje tap-target alespoň 48×48 px s rozestupy.',
    how: [
      'Odkazům a tlačítkům dejte čitelné písmo (ideálně 14px+).',
      'Zajistěte dostatečnou klikací plochu (min. ~44–48 px) přes padding, ne jen velikost textu.',
      'Mezi sousedními klikacími prvky nechte rozestup (např. 8px+), ať se nepřekrývají prsty.',
      'Na mobilu testujte palcem, ne myší.',
    ],
    examples: [
      { good: '.btn, nav a {\n  font-size: 1rem;\n  padding: 12px 16px;   /* dostatečná klikací plocha */\n  display: inline-block;\n}' },
    ],
    conclusion: 'Klikací prvky dělejte velkoryse — čitelné písmo, dostatečný padding a rozestupy. Přesnou velikost tap-targetu ověříte jen v prohlížeči, ale drobné klikací texty jsou jasný signál k opravě. Méně chybných kliknutí = víc konverzí.',
    related: ['tiny_fonts', 'tap-targets', 'mobile'],
  },
  {
    id: 'relative_units',
    category: 'ux',
    title: 'Relativní jednotky písma (rem/em vs px)',
    short: 'Preferujte rem/em před pevnými px u velikostí písma.',
    impact: 'low',
    difficulty: 'medium',
    effort: '30–60 min',
    what: 'Velikost písma lze zadat pevně v px, nebo relativně v rem/em. Rem vychází ze základní velikosti dokumentu, takže reaguje na uživatelské nastavení velikosti písma v prohlížeči.',
    why: 'Pevné px ignorují, když si uživatel v prohlížeči zvětší výchozí písmo (časté u lidí se slabším zrakem). Rem/em toto nastavení respektují — jsou přístupnější a snáz se škálují konzistentně.',
    how: [
      'Nastavte základní velikost na <html> (typicky 100% = 16px).',
      'Velikosti písma zadávejte v rem (např. 1rem, 1.25rem, 0.875rem).',
      'em používejte tam, kde má velikost vycházet z rodiče.',
      'Pevné px nechte jen pro detaily, kde škálování nedává smysl (např. 1px linky).',
    ],
    examples: [
      { bad: 'h1 { font-size: 32px; }\np  { font-size: 14px; }', good: 'html { font-size: 100%; }\nh1 { font-size: 2rem; }\np  { font-size: 0.95rem; }' },
    ],
    conclusion: 'Relativní jednotky jsou přístupnější a udržovatelnější. Není to havárie jako blokovaný zoom, ale u nového webu se vyplatí dělat to rovnou správně přes rem. Respektuje to uživatele, kteří si zvětšují písmo.',
    related: ['tiny_fonts', 'zoom'],
  },
  {
    id: 'contrast',
    category: 'ux',
    title: 'Kontrast textu vůči pozadí',
    short: 'Text musí mít dostatečný kontrast (WCAG AA 4,5:1).',
    impact: 'high',
    difficulty: 'easy',
    effort: '20–40 min',
    what: 'Kontrast je rozdíl světlosti mezi textem a pozadím. Světle šedý text na bílém pozadí má nízký kontrast a špatně se čte. WCAG AA vyžaduje poměr alespoň 4,5:1 pro běžný text.',
    why: 'Nízký kontrast vyřazuje uživatele se slabším zrakem a zhoršuje čitelnost pro všechny, zvlášť na mobilu na slunci. Je to častý přístupnostní nedostatek a Google ho v auditech hlásí.',
    how: [
      'Ověřte poměr kontrastu (nástroj v prohlížeči nebo online kontrast checker).',
      'Pro běžný text cílte na ≥ 4,5:1, pro velký text ≥ 3:1.',
      'Vyhněte se světle šedému textu (#ccc, #ddd) na bílém pozadí.',
      'Netlumte text příliš nízkou průhledností (opacity).',
    ],
    examples: [
      { bad: '.text { color: #bbbbbb; background: #ffffff; } /* ~1.9:1 — nečitelné */', good: '.text { color: #374151; background: #ffffff; } /* ~10:1 — OK */' },
    ],
    conclusion: 'Dostatečný kontrast je základ čitelnosti i přístupnosti. Naše statická heuristika upozorní na zjevně světlé barvy, ale přesný poměr ověřte v prohlížeči. Tmavší text na světlém pozadí (a naopak) je bezpečná volba.',
    related: ['tiny_fonts', 'accessibility'],
  },
  {
    id: 'responsive_images',
    category: 'ux',
    title: 'Responzivní obrázky',
    short: 'Obrázky se musí vejít na mobil bez vodorovného posuvu.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '10–20 min',
    what: 'Obrázek s pevnou velkou šířkou (např. width="1200") může na úzkém mobilu přetéct mimo obrazovku a způsobit vodorovné posouvání celé stránky.',
    why: 'Vodorovný posuv je jeden z nejotravnějších UX problémů na mobilu — rozbíjí layout a působí nedodělaně. Snadno se mu předejde jediným pravidlem v CSS.',
    how: [
      'Dejte obrázkům max-width: 100% a height: auto.',
      'Uvádějte width a height v HTML kvůli stabilitě layoutu (CLS), ale omezte je v CSS.',
      'Pro různé velikosti zvažte srcset / <picture>.',
      'Otestujte na úzké šířce (~360px), zda se nic neposouvá vodorovně.',
    ],
    examples: [
      { good: 'img { max-width: 100%; height: auto; }' },
    ],
    conclusion: 'Jediné pravidlo (max-width:100%; height:auto) vyřeší většinu přetékání obrázků na mobilu. Doplňte rozměry v HTML kvůli CLS a u velkých vizuálů zvažte srcset. Rychlá oprava častého mobilního neduhu.',
    related: ['img-dimensions', 'lazy-load-images', 'viewport'],
  },
]

// ─────────────────────────────────────────────────────────────────────────
// NÁSTROJE — témata na úrovni jednotlivých SEO nástrojů (ToolsPage)
// ─────────────────────────────────────────────────────────────────────────

const TOOL_ENTRIES: KbEntry[] = [
  {
    id: 'sitemap',
    category: 'seo',
    title: 'XML Sitemapa',
    short: 'Seznam URL pro vyhledávače — pomáhá rychlejší indexaci.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '15–30 min',
    what: 'XML sitemapa je soubor (obvykle /sitemap.xml) se seznamem všech důležitých URL webu. Vyhledávače podle ní rychleji a úplněji objeví obsah.',
    why: 'Bez sitemapy se Google spoléhá jen na odkazy — nové nebo hluboko zanořené stránky může objevit pomalu nebo vůbec. Sitemapa urychluje indexaci, hlavně u větších a nových webů.',
    how: [
      'Vygenerujte sitemap.xml se všemi indexovatelnými URL (CMS to obvykle umí samo).',
      'Uvádějte jen kanonické, indexovatelné stránky (ne noindex, ne přesměrování).',
      'Odkaz na sitemapu vložte do robots.txt a nahrajte ji v Search Console.',
      'Udržujte ji aktuální — automaticky při přidání/smazání stránky.',
    ],
    examples: [
      { caption: 'Odkaz v robots.txt', good: 'Sitemap: https://www.example.cz/sitemap.xml' },
    ],
    conclusion: 'Sitemapa je levná pojistka pro indexaci. U malého statického webu není kritická, u e-shopu nebo webu s tisíci stránek je nutností. Hlavně ji držte aktuální a bez neindexovatelných URL.',
    related: ['robots', 'canonical', 'links'],
  },
  {
    id: 'serp',
    category: 'seo',
    title: 'Náhled ve výsledcích (SERP snippet)',
    short: 'Jak stránka vypadá ve výsledcích Google.',
    impact: 'medium',
    difficulty: 'easy',
    effort: '10–20 min',
    what: 'SERP snippet je náhled stránky ve výsledcích vyhledávání — titulek, zelená URL a popisek. Tvoří ho title, meta description a struktura URL (plus rich results ze strukturovaných dat).',
    why: 'Snippet je první dojem a rozhoduje o prokliku. I dobře umístěná stránka přijde o návštěvy, když má neatraktivní nebo oříznutý titulek a popis.',
    how: [
      'Vylaďte title (50–60 znaků) a meta description (120–158 znaků).',
      'Hlídejte délku, ať se nic neořízne.',
      'Strukturovanými daty získejte rich results (hvězdičky, ceny, FAQ).',
      'Otestujte náhled v simulátoru SERP před publikací.',
    ],
    conclusion: 'Snippet je tvoje reklama zdarma ve výsledcích. Pravidelně kontroluj, jak se zobrazuje, a laď title/description podle prokliku. Rich results z strukturovaných dat ho posunou ještě výš.',
    related: ['title', 'meta_description', 'structured_data'],
  },
  {
    id: 'hreflang',
    category: 'seo',
    title: 'Hreflang (vícejazyčné weby)',
    short: 'Říká Googlu, která jazyková verze patří kterému publiku.',
    impact: 'medium',
    difficulty: 'hard',
    effort: 'náročné nastavení',
    what: 'Hreflang je značka, která u vícejazyčného/vícezemního webu určuje, jaká jazyková a regionální verze stránky existuje (např. cs, sk, en-us).',
    why: 'Bez hreflang může Google zobrazit nesprávnou jazykovou verzi nebo verze považovat za duplicity. Správný hreflang zajistí, že Čech vidí českou a Slovák slovenskou verzi.',
    how: [
      'Ke každé verzi přidejte hreflang odkazy na všechny ostatní verze i na sebe (self).',
      'Používejte správné kódy jazyka (a případně země): cs, sk, en-us…',
      'Doplňte x-default pro výchozí verzi.',
      'Hreflang musí být vzájemný (A odkazuje na B i B na A) a ukazovat na kanonické URL.',
    ],
    examples: [
      { good: '<link rel="alternate" hreflang="cs" href="https://example.cz/">\n<link rel="alternate" hreflang="sk" href="https://example.sk/">\n<link rel="alternate" hreflang="x-default" href="https://example.cz/">' },
    ],
    conclusion: 'Hreflang řeš jen u skutečně vícejazyčných/vícezemních webů — tam je ale klíčový a snadno se v něm chybuje (musí být vzájemný a kompletní). Jednojazyčný web ho nepotřebuje. Po nasazení ověř v Search Console.',
    related: ['language', 'canonical'],
  },
  {
    id: 'mobile',
    category: 'ux',
    title: 'Mobilní přívětivost',
    short: 'Google hodnotí primárně mobilní verzi (mobile-first).',
    impact: 'critical',
    difficulty: 'medium',
    effort: 'dle stavu webu',
    what: 'Mobilní přívětivost znamená, že se web na telefonu dobře zobrazuje a ovládá — responzivní layout, čitelné písmo, dostatečně velká tlačítka, žádné vodorovné posouvání.',
    why: 'Google indexuje mobile-first a většina návštěv je z mobilů. Web, který je na telefonu nepoužitelný, ztrácí pozice i návštěvníky bez ohledu na to, jak vypadá na počítači.',
    how: [
      'Nastavte správně meta viewport a používejte responzivní CSS.',
      'Zajistěte čitelné písmo (16px+) a tap-targety alespoň ~44–48 px.',
      'Nezakazujte zoom a zabraňte vodorovnému přetékání obsahu.',
      'Otestujte na reálných telefonech i v testu mobilní použitelnosti.',
    ],
    conclusion: 'Mobil je dnes primární — ber ho jako hlavní, ne jako „taky verzi". Pokryje to viewport, responzivita, velikost písma a tap-targetů. Detaily najdeš v UX/UI auditu. Na mobilní přívětivosti dnes stojí a padá viditelnost.',
    related: ['viewport', 'zoom', 'tiny_fonts', 'clickable_fonts', 'responsive_images'],
  },
  {
    id: 'security',
    category: 'security',
    title: 'Zabezpečení webu',
    short: 'HTTPS, bezpečnostní hlavičky a ochrana návštěvníků.',
    impact: 'high',
    difficulty: 'medium',
    effort: '30–60 min',
    what: 'Bezpečnost webu zahrnuje HTTPS, bezpečnostní HTTP hlavičky (HSTS, X-Content-Type-Options, X-Frame-Options, CSP) a ochranu před běžnými útoky.',
    why: 'Nezabezpečený web ohrožuje návštěvníky i tvoji značku a Google ho znevýhodňuje. Bezpečnostní hlavičky brání útokům jako clickjacking, MIME sniffing nebo injektáž skriptů.',
    how: [
      'Mějte platné HTTPS a vynucujte ho (HSTS).',
      'Nastavte X-Content-Type-Options: nosniff a X-Frame-Options (nebo CSP frame-ancestors).',
      'Zvažte Content-Security-Policy proti XSS.',
      'Udržujte CMS, knihovny a pluginy aktualizované.',
    ],
    examples: [
      { caption: 'Základní bezpečnostní hlavičky', good: 'Strict-Transport-Security: max-age=31536000; includeSubDomains\nX-Content-Type-Options: nosniff\nX-Frame-Options: SAMEORIGIN' },
    ],
    conclusion: 'Bezpečnost je důvěra. Začni HTTPS + HSTS a základními hlavičkami (rychlá výhra), CSP přidej s rozmyslem (umí rozbít stránku, nasazuj postupně). Aktualizace systému je průběžná samozřejmost.',
    related: ['https', 'http', 'dns'],
  },
  {
    id: 'redirects',
    category: 'seo',
    title: 'Přesměrování (redirecty)',
    short: 'Krátké řetězce 301, žádné smyčky.',
    impact: 'medium',
    difficulty: 'medium',
    effort: '20–40 min',
    what: 'Přesměrování posílá uživatele i vyhledávače z jedné URL na jinou. 301 = trvalé (předává SEO sílu), 302 = dočasné. Řetězec = více přesměrování za sebou.',
    why: 'Dlouhé řetězce přesměrování zpomalují načítání a rozmělňují link equity. Smyčka přesměrování stránku úplně znepřístupní. Špatný typ (302 místo 301) nepředá SEO sílu.',
    how: [
      'Pro trvalé přesunutí používejte 301, ne 302.',
      'Zkraťte řetězce na jediný skok (A→C, ne A→B→C).',
      'Odstraňte smyčky přesměrování.',
      'Po změně URL vždy nastavte 301 ze staré na novou adresu.',
    ],
    conclusion: 'Přesměrování drž čistá: 301 pro trvalé změny, jeden skok, nula smyček. Při migracích webu je správné mapování 301 zásadní, ať neztratíš pozice ani odkazy. Pravidelně kontroluj řetězce.',
    related: ['http', 'url_structure', 'canonical'],
  },
  {
    id: 'technology',
    category: 'seo',
    title: 'Použité technologie',
    short: 'Přehled CMS, frameworků a knihoven webu.',
    impact: 'low',
    difficulty: 'easy',
    effort: 'informační',
    what: 'Detekce technologií zjistí, na čem web běží — CMS (WordPress, Shopify…), frontend framework, analytika, CDN, serverový software apod.',
    why: 'Samo o sobě to není SEO problém, ale je užitečné pro audit: zastaralé verze mohou mít bezpečnostní díry, těžké frameworky zpomalují, a znalost stacku pomáhá při optimalizaci a údržbě.',
    how: [
      'Zkontrolujte, zda nejsou použité knihovny/CMS zastaralé (bezpečnost, výkon).',
      'Zvažte, zda těžké skripty třetích stran (widgety, trackery) za to stojí.',
      'U pomalého webu se podívejte, kolik a jakých technologií se načítá.',
    ],
    conclusion: 'Tohle je spíš informační přehled než seznam chyb. Využij ho k odhalení zastaralých nebo zbytečně těžkých komponent — ty pak řeš v rámci rychlosti a bezpečnosti.',
    related: ['performance', 'security', 'reduce-js-files'],
  },
  {
    id: 'accessibility',
    category: 'ux',
    title: 'Přístupnost (WCAG)',
    short: 'Web použitelný i pro lidi s hendikepem.',
    impact: 'high',
    difficulty: 'medium',
    effort: 'průběžně',
    what: 'Přístupnost (a11y) znamená, že web mohou používat i lidé se zrakovým, sluchovým či motorickým hendikepem — přes čtečky obrazovky, klávesnici apod. Standardem je WCAG.',
    why: 'Kromě etického a často i zákonného důvodu přístupnost zlepšuje SEO a UX pro všechny: alt texty, dobrá struktura nadpisů, popisné odkazy a kontrast pomáhají i vyhledávačům. Nepřístupný web vyřazuje část publika.',
    how: [
      'Doplňte alt texty obrázkům a labely formulářům.',
      'Udržujte správnou hierarchii nadpisů (jeden H1, bez přeskoků).',
      'Zajistěte dostatečný kontrast a ovladatelnost klávesnicí.',
      'Používejte popisné texty odkazů (ne „klikněte zde").',
    ],
    conclusion: 'Přístupnost a SEO jdou ruku v ruce — co pomáhá čtečce obrazovky, pomáhá i Googlu. Začni od alt textů, nadpisů, kontrastu a popisných odkazů. Detailní kontrolu dělá nástroj Přístupnost a UX audit.',
    related: ['images', 'headings', 'links', 'contrast', 'ux'],
  },
  {
    id: 'cookies',
    category: 'security',
    title: 'Cookies & GDPR',
    short: 'Souhlas s cookies a ochrana osobních údajů.',
    impact: 'medium',
    difficulty: 'medium',
    effort: '30–60 min',
    what: 'Weby sbírající cookies (zejména analytické a marketingové) musí podle GDPR/ePrivacy získat souhlas uživatele a mít zásady ochrany osobních údajů. Řeší to cookie lišta a privacy policy.',
    why: 'Bez správného souhlasu hrozí právní postih a pokuty. Tracking skripty (analytika, pixely) se nesmí spustit dřív, než uživatel udělí souhlas. Je to povinnost, ne volba.',
    how: [
      'Nasaďte cookie lištu se skutečnou volbou (přijmout/odmítnout, ne jen „OK").',
      'Tracking skripty spouštějte až PO udělení souhlasu.',
      'Mějte dostupné zásady ochrany osobních údajů a zpracování cookies.',
      'Ukládejte doklad o souhlasu.',
    ],
    conclusion: 'Cookies a GDPR ber jako právní povinnost, ne kosmetiku. Klíčové je nespouštět tracking před souhlasem a mít funkční volbu odmítnutí. Při nejistotě konzultuj znění s právníkem — pokuty jsou reálné.',
    related: ['security', 'technology'],
  },
  {
    id: 'dns',
    category: 'security',
    title: 'DNS & SSL certifikát',
    short: 'Správné DNS záznamy a platný SSL certifikát.',
    impact: 'medium',
    difficulty: 'medium',
    effort: 'dle stavu',
    what: 'DNS překládá doménu na server, SSL certifikát zajišťuje HTTPS. Kontrola zahrnuje platnost a správnost certifikátu, DNS záznamy a e-mailové ochrany (SPF, DMARC).',
    why: 'Prošlý nebo špatně nasazený certifikát zobrazí návštěvníkům bezpečnostní varování a odradí je. Chybějící SPF/DMARC umožňuje podvodníkům posílat e-maily jménem tvé domény (phishing).',
    how: [
      'Hlídejte platnost SSL certifikátu a automatickou obnovu (Let\'s Encrypt).',
      'Ověřte, že certifikát pokrývá doménu i www variantu a celý řetězec je správný.',
      'Nastavte SPF a DMARC záznamy pro ochranu e-mailů.',
      'Zkontrolujte správnost A/CNAME záznamů.',
    ],
    conclusion: 'DNS a SSL jsou „instalatérská" základna webu — když fungují, nevidíš je; když ne, je problém hned. Hlavní je automatická obnova certifikátu a nastavené SPF/DMARC. Vyplatí se hlídat expiraci certifikátu předem.',
    related: ['https', 'security'],
  },
  {
    id: 'broken_links',
    category: 'seo',
    title: 'Nefunkční odkazy (broken links)',
    short: 'Odkazy vedoucí na 404 kazí UX i crawl.',
    impact: 'medium',
    difficulty: 'easy',
    effort: 'průběžně',
    what: 'Nefunkční odkaz vede na neexistující stránku (404) nebo chybu. Může být interní (v rámci webu) i externí (na cizí web, který zmizel).',
    why: 'Nefunkční odkazy frustrují návštěvníky, plýtvají crawl budgetem a signalizují zanedbanou údržbu. Interní broken links navíc brání Googlu projít web a předávat autoritu.',
    how: [
      'Pravidelně proskenujte web na nefunkční odkazy.',
      'Interní 404 opravte — uprav odkaz nebo nastav 301 na náhradu.',
      'Externí mrtvé odkazy aktualizuj nebo odeber.',
      'Hlídejte to hlavně po migracích a změnách URL.',
    ],
    conclusion: 'Nefunkční odkazy jsou tichý únik kvality i návštěvnosti. Náprava je snadná, jen vyžaduje pravidelnost. Po každé větší změně webu udělej kontrolu broken links — interní řeš přednostně.',
    related: ['links', 'http', 'redirects'],
  },
  {
    id: 'geo',
    category: 'geo',
    title: 'GEO — optimalizace pro AI vyhledávače',
    short: 'Aby tě citovaly ChatGPT, Gemini, Perplexity a další.',
    impact: 'high',
    difficulty: 'medium',
    effort: 'průběžně',
    what: 'GEO (Generative Engine Optimization) je optimalizace pro AI vyhledávače a asistenty. Řeší přístup AI botů, soubor llms.txt, citovatelnost obsahu (FAQ, statistiky, jasné odpovědi) a strukturovaná data pro AI.',
    why: 'Stále víc lidí hledá přes AI (ChatGPT, Perplexity, Gemini), které čerpají z webů a citují je. Kdo není pro AI srozumitelný a přístupný, mizí z tohoto rychle rostoucího kanálu.',
    how: [
      'Povolte přístup relevantním AI botům (robots.txt) — pokud chcete být citováni.',
      'Zvažte soubor /llms.txt s přehledem klíčového obsahu.',
      'Pište citovatelně: jasné odpovědi, FAQ, statistiky, seznamy, definice.',
      'Posilte strukturovaná data (FAQPage, Article, Speakable) a E-E-A-T signály.',
    ],
    conclusion: 'GEO je nová vrstva nad klasickým SEO — cílí na to, aby tě AI nejen našla, ale i pochopila a citovala. Základ je přístup AI botů, citovatelný obsah a strukturovaná data. Kdo to podchytí teď, získá náskok.',
    related: ['structured_data', 'robots', 'content_quality'],
  },
]

// Registry + pořadí pro navigaci Předchozí/Další ----------------------------

// Překlad obsahu — slug/id, kategorie, dopad a náročnost zůstávají z české předlohy,
// textová pole se přepíšou z jazykového JSON (pokud existuje pro aktivní jazyk).
const KB_TRANS: Record<string, any[]> = { en: kbEn, de: kbDe, pl: kbPl, sk: kbSk, it: kbIt, hu: kbHu, uk: kbUk, es: kbEs, fr: kbFr, nl: kbNl, ja: kbJa }
function localizeKb(entries: KbEntry[]): KbEntry[] {
  const tr = KB_TRANS[KB_LANG]
  if (!tr) return entries
  const map = new Map(tr.map((e) => [e.id, e]))
  return entries.map((e) => {
    const t = map.get(e.id)
    return t ? { ...e, ...t } : e
  })
}

export const KB_ENTRIES: KbEntry[] = localizeKb([
  ...SEO_ENTRIES,
  ...PERF_ENTRIES,
  ...UX_ENTRIES,
  ...TOOL_ENTRIES,
])

/** Mapování id SEO nástroje (ToolsPage) → id tématu ve znalostní bázi. */
export const TOOL_KB: Record<string, string> = {
  robots: 'robots',
  sitemap: 'sitemap',
  schema: 'structured_data',
  serp: 'serp',
  hreflang: 'hreflang',
  mobile: 'mobile',
  security: 'security',
  pagespeed: 'performance',
  redirects: 'redirects',
  content: 'content_quality',
  technology: 'technology',
  accessibility: 'accessibility',
  ux: 'zoom',
  cookies: 'cookies',
  dns: 'dns',
  'broken-links': 'broken_links',
  geo: 'geo',
}

export const KB_MAP: Record<string, KbEntry> = Object.fromEntries(
  KB_ENTRIES.map((e) => [e.id, e]),
)

export function getKbEntry(id: string): KbEntry | undefined {
  return KB_MAP[id]
}

/** Vrátí seznam id, která mají záznam (pro filtrování „Zjistit více"). */
export function hasKb(id: string): boolean {
  return id in KB_MAP
}
